import { useEffect, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { TruncatedTypography } from 'src/components/TruncatedTypography';
import { useEffectOnce } from 'src/lib/hooks';

import { InsightBox } from '../InsightBox';

import { EmptyFormatsBanner } from './components/EmptyFormatsBanner';
import { FormatComplianceIssues } from './components/FormatComplianceIssues';
import { FormatCompliantFormats } from './components/FormatCompliantFormats';
import { FormatSelectionPopup } from './components/FormatSelectionPopup';
import { Loader } from './components/Loader';
import { useStyles } from './useStyles';

const propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({}),
  customFormatViolations: PropTypes.array,
  customFormatLoading: PropTypes.bool,
  customFormatIds: PropTypes.array,
  onFormatFixClick: PropTypes.func,
  onCustomFormatIdsChange: PropTypes.func,
  selectedFormatIds: PropTypes.array,
  onSelectedFormatIdsChange: PropTypes.func,
  customFormats: PropTypes.array,
  availableFormats: PropTypes.array,
  readOnly: PropTypes.bool,
  onMounted: PropTypes.func.isRequired,
};

const defaultProps = {
  className: null,
  classes: null,
  customFormatViolations: [],
  customFormatLoading: false,
  customFormatIds: [],
  onFormatFixClick: null,
  onCustomFormatIdsChange: null,
  selectedFormatIds: [],
  onSelectedFormatIdsChange: null,
  customFormats: [],
  availableFormats: [],
  readOnly: false,
};

export const Format = ({
  className,
  classes,
  customFormatViolations,
  customFormatLoading,
  customFormatIds,
  onFormatFixClick,
  onCustomFormatIdsChange,
  selectedFormatIds,
  onSelectedFormatIdsChange,
  customFormats,
  availableFormats,
  readOnly,
  onMounted,
}) => {
  const styles = useStyles({ classes });

  // Use the available formats passed from parent
  const formats = availableFormats || [];

  // Local state for popup
  const [formatPopupOpen, setFormatPopupOpen] = useState(false);

  // Notify parent when selected format IDs change
  useEffect(() => {
    onCustomFormatIdsChange?.(selectedFormatIds);
  }, [selectedFormatIds, onCustomFormatIdsChange]);

  const handleSettingsClick = () => setFormatPopupOpen(true);
  const handlePopupApply = (ids) => {
    if (onSelectedFormatIdsChange) {
      onSelectedFormatIdsChange(ids);
    }
    setFormatPopupOpen(false);
  };
  const handlePopupCancel = () => setFormatPopupOpen(false);

  // Only show/evaluate selected formats
  const shownCustomFormats = customFormats.filter(
    (f) => selectedFormatIds.includes(f.id) && customFormatIds.includes(f.id)
  );

  // Filter violations and compliant formats by selection
  const shownCustomFormatViolations = customFormatViolations.filter((v) =>
    selectedFormatIds.includes(v.formatId)
  );
  const compliantFormats = shownCustomFormats
    .filter((f, index, self) => index === self.findIndex((t) => t.id === f.id))
    .filter((f) => !shownCustomFormatViolations.some((v) => v.formatId === f.id))
    .map((f) => ({ name: f.name, type: 'custom-format' }));
  const violations = shownCustomFormatViolations.map((v) => ({
    ...v,
    formatName: shownCustomFormats.find((f) => f.id === v.formatId)?.name || '',
    formatType: `custom-format-${v.formatId}`,
    onFixAllClick: onFormatFixClick ? onFormatFixClick(v.formatId) : null,
  }));

  // Check for empty state: no formats available OR no formats are selected
  const hasNoFormats = formats.length === 0;
  const noFormatsSelected = selectedFormatIds.length === 0;
  const isEmptyState = hasNoFormats || noFormatsSelected;

  useEffectOnce(() => {
    onMounted();
  });

  return (
    <div
      className={cx(styles.root, className)}
      data-section="format"
    >
      <div className={styles.header}>
        <TruncatedTypography
          className={styles.boxGroupTitle}
          variant={TYPOGRAPHY_VARIANT.medium12}
        >
          Format
        </TruncatedTypography>
      </div>

      <InsightBox
        className={styles.wrapper}
        onSettingsClick={handleSettingsClick}
      >
        {isEmptyState ? (
          <EmptyFormatsBanner
            onCtaClick={handleSettingsClick}
            availableFormatsCount={formats.length}
            selectedFormatIds={selectedFormatIds}
          />
        ) : customFormatLoading ? (
          <Loader />
        ) : (
          <>
            <FormatCompliantFormats compliantFormats={compliantFormats} />
            <div className={styles.sectionSpacing}>
              <FormatComplianceIssues
                violations={violations}
                readOnly={readOnly}
              />
            </div>
          </>
        )}
      </InsightBox>

      {customFormatLoading && (
        <div className={styles.loadingText}>
          <CustomTypography variant={TYPOGRAPHY_VARIANT.regular12}>
            Analyzing text for format compliance, this might take a few seconds...
          </CustomTypography>
        </div>
      )}

      <FormatSelectionPopup
        open={formatPopupOpen}
        onApply={handlePopupApply}
        onCancel={handlePopupCancel}
        formats={formats}
        selectedFormatIds={selectedFormatIds}
      />
    </div>
  );
};

Format.propTypes = propTypes;
Format.defaultProps = defaultProps;
